
import { Component, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { UserController, UserEntity } from '@domain';
import { take } from 'rxjs';
import { OrderMainService } from '../../pages/main/services/order-main.service';
import { RaLoadingComponent, RaUserAvatarComponent } from '@shared/components';
import { Subscription } from 'rxjs';
import { RaSwitchComponent } from 'ra-ui';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';

@Component({
    selector: 'modal-members',
    templateUrl: 'modal-members.component.html',
    styleUrl: 'modal-members.component.scss',
    imports: [
    RaModalComponent,
    RaLoadingComponent,
    RaEmptyComponent,
    RaSwitchComponent,
    RaUserAvatarComponent,
    RaIconComponent,
    ReactiveFormsModule,
    TranslatePipe,
],
})
export class ModalMembersComponent implements OnDestroy {
    public currentEmployees = input.required<UserEntity[]>();
    public close = output<void>();
    public loading = signal(false);
    public members = signal<UserEntity[]>([]);

    /** Membros com salvamento em andamento (switch travado até responder). */
    public savingIds = signal<Set<string>>(new Set());

    // Map para armazenar os FormControls de cada membro
    public memberControlsMap = signal<Map<string, FormControl>>(new Map());

    // Map para armazenar o estado inicial de cada membro (se estava ativo inicialmente)
    private _initialStateMap = new Map<string, boolean>();

    // Subscriptions para os FormControls
    private _controlSubscriptions = new Map<string, Subscription>();

    // * [Injects]
    private _userController = inject(UserController);
    private _orderMainService = inject(OrderMainService);

    /** Usuário logado — ganha o marcador i18n `order.members.you` na lista. */
    public currentUserId = '';

    constructor() {
        // Reage a mudanças no currentEmployees e atualiza os checkboxes
        effect(() => {
            const currentEmployees = this.currentEmployees();
            const members = this.members();
            const controlsMap = this.memberControlsMap();

            // Se já tiver membros mas ainda não inicializou os controles, inicializa
            if (members.length > 0 && controlsMap.size === 0) {
                this._initializeMemberControls(members);
            }
            // Se já tiver membros e controles inicializados, atualiza
            else if (members.length > 0 && controlsMap.size > 0) {
                this._updateMemberControls(members, currentEmployees);
            }
        });
    }

    ngOnInit(): void {
        this.currentUserId = this._userController.getCurrentUserId();
        this._getMembers();
    }

    private _getMembers(): void {
        this.loading.set(true);
        // Equipe COMPLETA, incluindo o usuário logado (o `getEmployees` o exclui
        // — serve à gestão de funcionários; aqui todo mundo é elegível ao serviço).
        this._userController.getAllTeam().subscribe({
            next: members => {
                this.members.set(members);
                // A inicialização será feita pelo effect quando ambos estiverem disponíveis
                // Mas também inicializa aqui caso o effect não tenha sido executado ainda
                if (this.memberControlsMap().size === 0) {
                    this._initializeMemberControls(members);
                }
                this.loading.set(false);
            },
            // Sem isto o spinner ficava girando para sempre quando a busca falhava
            error: () => this.loading.set(false),
        });
    }

    private _initializeMemberControls(members: UserEntity[]): void {
        const currentEmployees = this.currentEmployees();
        const controlsMap = new Map<string, FormControl>();

        // Função auxiliar para normalizar IDs
        const normalizeId = (id: any): string => {
            if (!id) return '';
            // Se for objeto com propriedade toString, usa toString
            if (typeof id === 'object' && id.toString) {
                return String(id.toString());
            }
            return String(id);
        };

        // Cria um Set com os IDs dos funcionários atuais para comparação rápida
        const currentEmployeeIds = new Set(
            (currentEmployees || []).map(emp => normalizeId(emp._id))
        );

        members.forEach(member => {
            const memberId = normalizeId(member._id);

            // Verifica se o membro está na lista de funcionários atuais
            const isSelected = currentEmployeeIds.has(memberId);
            const control = new FormControl(isSelected);

            // Armazena o estado inicial
            this._initialStateMap.set(memberId, isSelected);

            // Adiciona listener para mudanças no checkbox
            this._subscribeToControlChanges(memberId, control);

            controlsMap.set(memberId, control);
        });

        this.memberControlsMap.set(controlsMap);
    }

    private _updateMemberControls(members: UserEntity[], currentEmployees: UserEntity[]): void {
        const controlsMap = this.memberControlsMap();

        // Função auxiliar para normalizar IDs
        const normalizeId = (id: any): string => {
            if (!id) return '';
            if (typeof id === 'object' && id.toString) {
                return String(id.toString());
            }
            return String(id);
        };

        const currentEmployeeIds = new Set(
            (currentEmployees || []).map(emp => normalizeId(emp._id))
        );

        members.forEach(member => {
            const memberId = normalizeId(member._id);
            const control = controlsMap.get(memberId);

            if (control) {
                const shouldBeSelected = currentEmployeeIds.has(memberId);
                // Só atualiza se o valor for diferente para evitar loops infinitos
                if (control.value !== shouldBeSelected) {
                    control.setValue(shouldBeSelected, { emitEvent: false });
                }
            }
        });
    }

    public getMemberControl(memberId: string): FormControl {
        const normalizedId = String(memberId);
        const control = this.memberControlsMap().get(normalizedId);

        if (!control) {
            const newControl = new FormControl(false);
            this._subscribeToControlChanges(normalizedId, newControl);
            return newControl;
        }

        return control;
    }

    private _subscribeToControlChanges(memberId: string, control: FormControl): void {
        // Remove subscription anterior se existir
        const existingSubscription = this._controlSubscriptions.get(memberId);
        if (existingSubscription) {
            existingSubscription.unsubscribe();
        }

        // Cria nova subscription para detectar mudanças
        const subscription = control.valueChanges.subscribe(newValue => {
            this._handleMemberToggle(memberId, newValue);
        });

        this._controlSubscriptions.set(memberId, subscription);
    }

    /**
     * SALVA NA HORA (auto-save): cada toggle dispara o update do serviço com
     * aquele único membro. O switch fica travado durante a requisição e, em
     * caso de erro, volta ao estado anterior (o toast de erro vem do controller).
     */
    private _handleMemberToggle(memberId: string, isActive: boolean): void {
        const initialState = this._initialStateMap.get(memberId) ?? false;
        if (initialState === isActive) return;

        this.savingIds.update(ids => new Set(ids).add(memberId));

        this._orderMainService
            .updateEmployees(isActive ? [memberId] : [], isActive ? [] : [memberId])
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this._initialStateMap.set(memberId, isActive);
                    this._removeSaving(memberId);

                    // Fonte da verdade: recarrega a order inteira do servidor para
                    // os avatares da equipe (e o resto do detalhe) refletirem já.
                    this._orderMainService.refreshCurrentOrder().pipe(take(1)).subscribe();
                },
                error: () => {
                    // Reverte o switch sem re-disparar o save
                    this.memberControlsMap()
                        .get(memberId)
                        ?.setValue(initialState, { emitEvent: false });
                    this._removeSaving(memberId);
                },
            });
    }

    private _removeSaving(memberId: string): void {
        this.savingIds.update(ids => {
            const next = new Set(ids);
            next.delete(memberId);
            return next;
        });
    }

    ngOnDestroy(): void {
        // Limpa todas as subscriptions
        this._controlSubscriptions.forEach(subscription => subscription.unsubscribe());
        this._controlSubscriptions.clear();
    }

}
